import { Injectable } from '@angular/core';
import { ApiResponse } from '../service/rest_api/rest_api';
import { environment } from '../../environments/environment';
import { MockScenarioService } from './mock-scenario.service';
import { MockScenarioState } from './mock-scenarios';

interface MockBusRow {
  destinationName: string;
  line: string | number;
  expectedDepartureTime: string;
  stand: string;
  dir: string;
  isMonitored: boolean;
  departureStatus: string;
}

@Injectable({
  providedIn: 'root'
})
export class MockApiService {
  private auth401OnceServed = false;
  private previousState: MockScenarioState;

  constructor(private mockScenarioService: MockScenarioService) {
    this.previousState = this.mockScenarioService.snapshot;
    this.mockScenarioService.state.subscribe((state) => {
      if (
        state.useRealApi !== this.previousState.useRealApi
        || state.scenarioId !== this.previousState.scenarioId
      ) {
        this.resetScenarioState();
      }
      this.previousState = state;
    });
  }

  public shouldMockBusData(): boolean {
    if (!environment.enableMockTools) {
      return false;
    }

    const { useRealApi, scenarioId } = this.mockScenarioService.snapshot;
    if (useRealApi) {
      return false;
    }

    return [
      'past-departures',
      'duplicate-buses',
      'weird-line-names',
      'mixed-quality',
      'empty-list'
    ].includes(scenarioId);
  }

  public async getBusData(limit: number): Promise<ApiResponse> {
    await this.applyLatency();

    const scenarioId = this.mockScenarioService.snapshot.scenarioId;
    const baseRows = this.buildBaseRows();
    let payload: MockBusRow[];

    switch (scenarioId) {
      case 'past-departures':
        payload = this.buildPastDeparturesScenario(baseRows);
        break;
      case 'duplicate-buses':
        payload = this.buildDuplicateBusesScenario(baseRows);
        break;
      case 'weird-line-names':
        payload = this.buildWeirdLineNamesScenario(baseRows);
        break;
      case 'mixed-quality':
        payload = this.buildMixedQualityScenario(baseRows);
        break;
      case 'empty-list':
        payload = [];
        break;
      default:
        payload = baseRows;
        break;
    }

    return new ApiResponse(200, 'success', true, payload.slice(0, limit));
  }

  public async interceptBusDataRequest(retrying: boolean): Promise<ApiResponse | null> {
    if (!environment.enableMockTools || this.mockScenarioService.snapshot.useRealApi) {
      return null;
    }

    await this.applyLatency();

    switch (this.mockScenarioService.snapshot.scenarioId) {
      case 'auth-401-once':
        if (!retrying && !this.auth401OnceServed) {
          this.auth401OnceServed = true;
          return new ApiResponse(401, 'Unauthorized', false, [{ message: 'Mock auth expired once.' }]);
        }
        return new ApiResponse(200, 'success', true, this.buildBaseRows());
      case 'auth-401-always':
        return new ApiResponse(401, 'Unauthorized', false, [{ message: 'Mock auth failure.' }]);
      case 'rate-limit':
        return new ApiResponse(429, 'Too Many Requests', false, [{ message: 'Mock rate limit hit.' }]);
      case 'network-offline':
        return new ApiResponse(0, 'cancelled', false, [{ message: "Can't connect to server. Please try again later." }]);
      case 'server-error':
        return new ApiResponse(500, 'Internal Server Error', false, [{ message: 'Mock upstream failure.' }]);
      case 'auth-endpoint-failure':
        return new ApiResponse(401, 'Unauthorized', false, [{ message: 'Mock auth endpoint is down.' }]);
      default:
        return null;
    }
  }

  public async interceptAuthRequest(): Promise<ApiResponse | null> {
    if (!environment.enableMockTools || this.mockScenarioService.snapshot.useRealApi) {
      return null;
    }

    await this.applyLatency();

    switch (this.mockScenarioService.snapshot.scenarioId) {
      case 'auth-endpoint-failure':
        return new ApiResponse(500, 'Internal Server Error', false, [{ message: 'Mock auth endpoint failure.' }]);
      case 'auth-401-once':
      case 'auth-401-always':
        return new ApiResponse(200, 'success', true, {
          token: 'mock-dev-token',
          RefreshToken: 'mock-dev-refresh-token',
          experatinTime: new Date(Date.now() + 3600000).toISOString()
        });
      default:
        return null;
    }
  }

  public resetScenarioState(): void {
    this.auth401OnceServed = false;
  }

  private async applyLatency(): Promise<void> {
    const { latencyMs } = this.mockScenarioService.snapshot;
    if (latencyMs <= 0) {
      return;
    }

    await new Promise((resolve) => setTimeout(resolve, latencyMs));
  }

  private buildBaseRows(): MockBusRow[] {
    return [
      this.makeRow(2, '1', 'Lancaster University', 'Stand 3', 'inbound'),
      this.makeRow(4, '4', 'Morecambe Bus Stn', 'Stand 7', 'outbound'),
      this.makeRow(7, '100', 'Preston Bus Stn', 'Stand 10', 'outbound', true, 'delayed'),
      this.makeRow(11, '41', 'Kendal & Keswick', 'Stand 12', 'outbound'),
      this.makeRow(14, '40', 'Lancaster Rail Stn', 'Stand 2', 'inbound'),
      this.makeRow(17, '5', 'Carnforth Rail Stn', 'Stand 9', 'outbound'),
      this.makeRow(23, '2A', 'Heysham via City', 'Stand 5', 'outbound'),
      this.makeRow(28, '55', 'KIRKBY LONSDALE Booths', 'Stand 14', 'outbound')
    ];
  }

  private buildPastDeparturesScenario(baseRows: MockBusRow[]): MockBusRow[] {
    return [
      this.makeRow(-6, '1', 'Lancaster University', 'Stand 3', 'inbound'),
      this.makeRow(-1, '4', 'Morecambe Bus Stn', 'Stand 7', 'outbound'),
      this.makeRow(0, '100', 'Preston Bus Stn', 'Stand 10', 'outbound', true, 'delayed'),
      this.makeRow(1, '41', 'Kendal & Keswick', 'Stand 12', 'outbound'),
      this.makeRow(6, '40', 'Lancaster Rail Stn', 'Stand 2', 'inbound'),
      ...baseRows.slice(5)
    ];
  }

  private buildDuplicateBusesScenario(baseRows: MockBusRow[]): MockBusRow[] {
    return [
      this.makeRow(2, '1', 'Lancaster University', 'Stand 3', 'inbound'),
      this.makeRow(5, '1', 'Lancaster University', 'Stand 3', 'inbound'),
      this.makeRow(9, '1', 'Lancaster University', 'Stand 3', 'inbound'),
      this.makeRow(3, '4', 'Morecambe Bus Stn', 'Stand 7', 'outbound'),
      this.makeRow(11, '4', 'Morecambe Bus Stn', 'Stand 7', 'outbound'),
      ...baseRows.slice(2)
    ];
  }

  private buildWeirdLineNamesScenario(baseRows: MockBusRow[]): MockBusRow[] {
    return [
      this.makeRow(2, '  n1  ', 'lancaster city centre, lancaster', 'Stand 1', 'outbound'),
      this.makeRow(5, 'U1*', 'heysham via city', 'Stand 2', 'outbound', true, 'delayed'),
      this.makeRow(8, 'X99 / night', 'KNOTT END Bourne Arms', 'Stand 3', 'outbound'),
      this.makeRow(11, '42A?', 'uni alexandra park', 'Stand 4', 'inbound'),
      ...baseRows.slice(4)
    ];
  }

  private buildMixedQualityScenario(baseRows: MockBusRow[]): MockBusRow[] {
    return [
      this.makeRow(9, '  5A  ', 'newlands farmdale road', '', 'outbound'),
      this.makeRow(1, '5A', 'NEWLANDS Farmdale Road', 'Stand 6', 'outbound'),
      this.makeRow(4, 'S1', 'Lancaster Bus Stati', 'Stand 1', 'inbound'),
      this.makeRow(6, 'S1', 'Lancaster Bus Stati', 'Stand 1', 'inbound', true, 'delayed'),
      this.makeRow(-2, '  N ', 'Marsh Circular', 'Stand 8', 'inbound'),
      ...baseRows.slice(3)
    ].sort((left, right) => right.expectedDepartureTime.localeCompare(left.expectedDepartureTime));
  }

  private makeRow(
    minutesFromNow: number,
    line: string | number,
    destinationName: string,
    stand: string,
    dir: string,
    isMonitored = true,
    departureStatus = 'ontime'
  ): MockBusRow {
    return {
      destinationName,
      line,
      expectedDepartureTime: new Date(Date.now() + minutesFromNow * 60000).toISOString(),
      stand,
      dir,
      isMonitored,
      departureStatus
    };
  }
}
